import React, {
  ReactNode,
  FC,
  useCallback,
  ElementType,
  ComponentProps,
  MouseEvent,
} from 'react';

import styled from 'styled-components';

import { getAccOrigin } from '@adsk/acs-sc-web-platform-api';
import * as analytics from '@adsk/acs-web-analytics';
import { useAlloyFeatureFlag } from '@adsk/alloy-provider';
import { Box } from '@adsk/alloy-react-box';
import { HomeIcon, RenderIconType } from '@adsk/alloy-react-icon';
import { MenuList, MenuItem } from '@adsk/alloy-react-menu';
import theme, { StylableComponent } from '@adsk/alloy-react-theme';

import { ANALYTICS_TYPES } from '../helpers/analytics';
import getAnchorRel from '../helpers/getAnchorRel';
import getAnchorTarget from '../helpers/getAnchorTarget';
import { Product } from '../helpers/types';

// TODO - can be removed when account entitlement is available in V2
// TODO - verify it works as expected
const getProductIconV1 = (product: { renderIcon?: RenderIconType }) =>
  product?.renderIcon?.({
    size: 24,
  });

const parser = (products?: Product[], selectedId?: string) =>
  products?.map((product) => ({
    ...product,
    id: product.key,
    url: product.defaultUrl,
    highlighted: product.key === selectedId,
  })) || [];

const MY_HOME_MENU_ITEM_ID = 'myHome';

const MenuItemText = styled.div({
  ...theme.typography.bodyMedium,
  ...theme.typography.ellipsis,
  flex: 1,
  paddingLeft: 10,
  textAlign: 'left',
});

const MenuItemWrapper = styled.div({
  borderBottom: `1px solid ${theme.colors.charcoal200}`,
});

type ProductPickerProps = StylableComponent<HTMLDivElement> & {
  products?: Product[];
  selectedId?: string;
  renderCustomHeader?: () => ReactNode;
  onProductClick?: (
    { id, url }: { id: string; url?: string },
    event: MouseEvent,
  ) => void;
  onClose?: () => void;
};

const DEFAULT_PROPS = {
  renderCustomHeader: () => null,
};

const ProductPicker: FC<ProductPickerProps> = ({
  className,
  style,
  products,
  selectedId,
  onClose,
  onProductClick,
  renderCustomHeader = DEFAULT_PROPS.renderCustomHeader,
}) => {
  const { value: showUserHomeTab } = useAlloyFeatureFlag('show-user-home-tab');

  const onProductClickWithAnalytic = useCallback(
    (id: string) => {
      analytics.track(
        ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_PRODUCT_CLICK,
        {
          product_id: id,
          prev_product_id: selectedId,
        },
      );
    },
    [selectedId],
  );

  const getMenuItemProps = ({
    id,
    url,
  }: {
    id: string;
    url?: string;
  }): ComponentProps<typeof MenuItem> => ({
    as: (onProductClick ? 'button' : 'a') as ElementType,
    'data-testid': `ProductPicker__product-${id}`,
    href: url,
    target: url && getAnchorTarget(url),
    rel: url && getAnchorRel(url),
    nested: false,
    showSelection: false,
    onClick: (event) => {
      onProductClick?.({ id, url }, event);
      onProductClickWithAnalytic(id);
      onClose?.();
    },
  });

  return (
    <Box className={className} width="300px" style={style}>
      <MenuList>
        {showUserHomeTab && (
          <MenuItemWrapper>
            <MenuItem
              {...getMenuItemProps({
                id: MY_HOME_MENU_ITEM_ID,
                url: `${getAccOrigin()}/home`,
              })}
              key={MY_HOME_MENU_ITEM_ID}
              highlighted={MY_HOME_MENU_ITEM_ID === selectedId}
              style={{ marginBottom: '8px' }}
            >
              <HomeIcon size={24} />
              <MenuItemText>My Home</MenuItemText>
            </MenuItem>
          </MenuItemWrapper>
        )}
        {renderCustomHeader()}
        {parser(products, selectedId).map(
          ({ id, name, url, highlighted, iconUrl, hasDivider, ...rest }) => (
            <MenuItem
              {...getMenuItemProps({ id, url })}
              key={id}
              highlighted={highlighted}
              style={{
                borderTop: hasDivider
                  ? `1px solid ${theme.colors.charcoal200}`
                  : 'none',
                height: 40,
                borderRadius: 0,
              }}
            >
              {iconUrl ? (
                <Box
                  bg={highlighted ? 'white' : 'charcoal900'}
                  width="24px"
                  height="24px"
                  style={{
                    maskImage: `url(${iconUrl})`,
                  }}
                />
              ) : (
                getProductIconV1(rest)
              )}
              <MenuItemText>{name}</MenuItemText>
            </MenuItem>
          ),
        )}
      </MenuList>
    </Box>
  );
};

ProductPicker.displayName = 'ProductPicker';

export default ProductPicker;
